import { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import { getPasses, getUsers, getUserDetails, deletePass, refreshPassesStatus, updatePass } from '../../services/adminService';
import { DaysOfTheWeekTrans, formatDateLocalized } from '../../utils/translations';
import { formatPassPriceShort } from '../../utils/currencyFormat';
import { getPassTypes } from '../../services/passService';
import UserDetailsModal from './users/UserDetailsModal';
import AddPassModal from './users/AddPassModal';
import EditPassModal from './EditPassModal';

export default function AdminPasses({ token, presetFilters }) {
  const { language, setToaster } = useAppContext();
  const daysTrans = DaysOfTheWeekTrans[language];
  const formatDate = (d) => formatDateLocalized(d, daysTrans);

  const [passes, setPasses] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState(''); // '', 'paid', 'unpaid'
  const [includeTest, setIncludeTest] = useState(false);
  const [passTypes, setPassTypes] = useState([]);
  const [passTypeFilter, setPassTypeFilter] = useState(''); // '' or passTypeId
  const [purchasedFrom, setPurchasedFrom] = useState('');
  const [purchasedTo, setPurchasedTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingPaidId, setUpdatingPaidId] = useState(null);
  const [refreshMessage, setRefreshMessage] = useState(null);
  const hasRefreshedStatuses = useRef(false);
  const [showAddPass, setShowAddPass] = useState(false);
  const [addPassUserId, setAddPassUserId] = useState(null);
  const [addPassUserSearch, setAddPassUserSearch] = useState('');
  const [addPassUserList, setAddPassUserList] = useState([]);
  const [addPassUserLoading, setAddPassUserLoading] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getPassTypes({ all: true })
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.passTypes || []);
        if (cancelled) return;
        setPassTypes(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setPassTypes([]);
      });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!presetFilters) return;
    if (presetFilters.status !== undefined) setStatusFilter(presetFilters.status || '');
    if (presetFilters.paid !== undefined) setPaymentFilter(presetFilters.paid || '');
    if (presetFilters.passTypeId !== undefined) setPassTypeFilter(presetFilters.passTypeId || '');
    if (presetFilters.purchasedFrom !== undefined) setPurchasedFrom(presetFilters.purchasedFrom || '');
    if (presetFilters.purchasedTo !== undefined) setPurchasedTo(presetFilters.purchasedTo || '');
    if (presetFilters.includeTest !== undefined) setIncludeTest(!!presetFilters.includeTest);
    setPage(1);
  }, [presetFilters]);

  useEffect(() => {
    if (!showAddPass || addPassUserId || !token) return;
    let cancelled = false;
    setAddPassUserLoading(true);
    getUsers(token, 1, addPassUserSearch)
      .then((data) => {
        if (!cancelled) setAddPassUserList(data.users || []);
      })
      .finally(() => {
        if (!cancelled) setAddPassUserLoading(false);
      });
    return () => { cancelled = true; };
  }, [showAddPass, addPassUserSearch, token, addPassUserId]);

  useEffect(() => {
    if (!token) return;
    const run = async () => {
      if (!hasRefreshedStatuses.current) {
        hasRefreshedStatuses.current = true;
        setLoading(true);
        setRefreshMessage(null);
        try {
          const data = await refreshPassesStatus(token);
          const parts = [];
          if (data.expired > 0) parts.push(`${data.expired} expired`);
          if (data.expiredSetToExhausted > 0) parts.push(`${data.expiredSetToExhausted} expired→exhausted`);
          if (data.expiredToExhausted > 0) parts.push(`${data.expiredToExhausted} expired set to exhausted`);
          if (data.exhausted > 0) parts.push(`${data.exhausted} exhausted`);
          if (data.restored > 0) parts.push(`${data.restored} restored to active`);
          setRefreshMessage(
            data.updated === 0
              ? 'No passes needed updating.'
              : `Updated: ${parts.join(', ')}.`
          );
          setTimeout(() => setRefreshMessage(null), 5000);
        } catch (error) {
          console.error('Error refreshing statuses:', error);
          setRefreshMessage('Failed to refresh statuses.');
          setLoading(false);
        }
      }
      await fetchPasses();
    };
    run();
  }, [token, page, statusFilter, paymentFilter, passTypeFilter, purchasedFrom, purchasedTo, includeTest]);

  const fetchPasses = async () => {
    setLoading(true);
    try {
      const data = await getPasses(token, {
        page,
        status: statusFilter,
        paid: paymentFilter,
        passTypeId: passTypeFilter,
        purchasedFrom,
        purchasedTo,
        includeTest,
      });
      const list = data.passes || [];
      setPasses(list);
      setPagination(data.pagination || null);
      return list;
    } catch (error) {
      console.error('Error fetching passes:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setDetailsLoading(true);
    try {
      const data = await getUserDetails(token, userId);
      setUserDetails(data);
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeUserModal = () => {
    setSelectedUserId(null);
    setUserDetails(null);
  };

  const handleTogglePaid = async (pass) => {
    const newPaidStatus = pass.paid !== false;
    const confirmMessage = newPaidStatus
      ? 'Mark this pass as not paid?'
      : 'Mark this pass as paid?';
    if (!window.confirm(confirmMessage)) return;
    setUpdatingPaidId(pass._id);
    try {
      await updatePass(token, pass._id, { paid: !newPaidStatus });
      const list = await fetchPasses();
      setSelectedPass((prev) => {
        if (!prev || prev._id !== pass._id) return prev;
        const updated = list.find((p) => p._id === pass._id);
        return updated || prev;
      });
      setToaster({
        message: `Pass marked as ${newPaidStatus ? 'not paid' : 'paid'}`,
        type: 'success',
        show: true,
      });
    } catch (error) {
      console.error('Error updating pass:', error);
      setToaster({
        message: 'Failed to update pass',
        type: 'error',
        show: true,
      });
    } finally {
      setUpdatingPaidId(null);
    }
  };

  const handleDeletePass = async (passId) => {
    if (!passId || !window.confirm('Delete this pass? Future bookings for this pass will be cancelled.')) return;
    setDeletingId(passId);
    try {
      await deletePass(token, passId);
      fetchPasses();
    } catch (error) {
      console.error('Error deleting pass:', error);
      throw error;
    } finally {
      setDeletingId(null);
    }
  };
  const handleDeletePassAndClose = async (passId) => {
    try {
      await handleDeletePass(passId);
      setSelectedPass(null);
    } catch (_) {}
  };

  const statusBadge = (pass) => (
    <span
      className={`px-2 py-0.5 rounded text-xs font-medium ${
        pass.status === 'active'
          ? 'bg-green-100 text-green-700'
          : pass.status === 'exhausted'
            ? 'bg-amber-100 text-amber-700'
            : pass.status === 'expired'
              ? 'bg-gray-100 text-gray-700'
              : pass.status === 'paused'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-orange-100 text-orange-700'
      }`}
    >
      {pass.status}
    </span>
  );

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm flex-1 sm:flex-initial min-w-0"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="exhausted">Exhausted</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
            <option value="paused">Paused</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">Payment:</label>
          <select
            value={paymentFilter}
            onChange={(e) => {
              setPaymentFilter(e.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm flex-1 sm:flex-initial min-w-0"
          >
            <option value="">All</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 shrink-0">Pass type:</label>
          <select
            value={passTypeFilter}
            onChange={(e) => {
              setPassTypeFilter(e.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm flex-1 sm:flex-initial min-w-0"
          >
            <option value="">All</option>
            {passTypes
              .slice()
              .sort((a, b) => {
                const an = (a.displayName || a.name || '').toString();
                const bn = (b.displayName || b.name || '').toString();
                return an.localeCompare(bn);
              })
              .map((pt) => (
                <option key={pt._id} value={pt._id}>
                  {pt.displayName || pt.name}
                </option>
              ))}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={includeTest}
            onChange={(e) => {
              setIncludeTest(e.target.checked);
              setPage(1);
            }}
          />
          Include test passes
        </label>
        <button
          type="button"
          onClick={() => { setShowAddPass(true); setAddPassUserId(null); setAddPassUserSearch(''); }}
          className="px-3 py-1.5 bg-brown text-white rounded text-sm hover:opacity-90 touch-manipulation"
        >
          Add pass
        </button>
        <button
          type="button"
          onClick={fetchPasses}
          disabled={loading}
          className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 touch-manipulation"
        >
          Refresh
        </button>
        {refreshMessage && (
          <span className="text-sm text-gray-600 w-full sm:w-auto">{refreshMessage}</span>
        )}
      </div>

      {loading ? (
        <p className="text-center py-6 text-gray-500 text-sm">Loading…</p>
      ) : (
        <>
          {/* Desktop: Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left text-sm border border-gray-200 rounded overflow-hidden min-w-[720px]">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="py-2 px-3 font-semibold">User</th>
                  <th className="py-2 px-3 font-semibold">Pass type</th>
                  <th className="py-2 px-3 font-semibold">Status</th>
                  <th className="py-2 px-3 font-semibold">Payment</th>
                  <th className="py-2 px-3 font-semibold">Purchased</th>
                  <th className="py-2 px-3 font-semibold">Expires</th>
                  <th className="py-2 px-3 font-semibold">Classes</th>
                  <th className="py-2 px-3 font-semibold">Price</th>
                  <th className="py-2 px-3 font-semibold">Subscription</th>
                  <th className="py-2 px-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {passes.map((pass) => (
                  <tr
                    key={pass._id}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedPass(pass)}
                  >
                    <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => handleViewUser(pass.userId?.user_id)}
                        className="text-left text-blue-600 hover:underline font-medium"
                      >
                        {pass.userId?.name || pass.userId?.email || '—'}
                      </button>
                    </td>
                    <td className="py-2 px-3">{pass.passTypeId?.displayName || pass.passTypeId?.name || '—'}</td>
                    <td className="py-2 px-3">{statusBadge(pass)}</td>
                    <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => handleTogglePaid(pass)}
                        disabled={updatingPaidId === pass._id}
                        title={pass.paid === false ? 'Click to mark as paid' : 'Click to mark as not paid'}
                        className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer hover:opacity-90 disabled:opacity-50 ${
                          pass.paid === false ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {updatingPaidId === pass._id ? '…' : (pass.paid === false ? 'Unpaid' : 'Paid')}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-xs">{formatDate(pass.purchasedAt) ?? '—'}</td>
                    <td className="py-2 px-3 text-xs">{formatDate(pass.expiresAt) ?? '—'}</td>
                    <td className="py-2 px-3">
                      {pass.classesIncluded != null
                        ? `${pass.classesUsed ?? 0} / ${pass.classesIncluded}`
                        : '∞'}
                    </td>
                    <td className="py-2 px-3">
                      {pass.finalPrice != null ? formatPassPriceShort(pass.finalPrice, pass.currency) : '—'}
                    </td>
                    <td className="py-2 px-3">
                      {pass.isSubscription ? (
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            pass.subscriptionActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {pass.subscriptionActive ? 'Yes' : 'No'}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                      {pass.isAdmin && (
                        <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700 mr-1">Test</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeletePass(pass._id)}
                        disabled={deletingId === pass._id}
                        className="text-red-600 hover:underline text-xs disabled:opacity-50"
                      >
                        {deletingId === pass._id ? '…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: Cards */}
          <div className="md:hidden space-y-3">
            {passes.map((pass) => (
              <div
                key={pass._id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedPass(pass)}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedPass(pass)}
                className="border border-gray-200 rounded-lg p-4 bg-white space-y-2 cursor-pointer hover:bg-gray-50"
              >
                <div className="flex justify-between items-start gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => handleViewUser(pass.userId?.user_id)}
                    className="text-left text-blue-600 hover:underline font-medium text-sm"
                  >
                    {pass.userId?.name || pass.userId?.email || '—'}
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {statusBadge(pass)}
                    <button
                      type="button"
                      onClick={() => handleTogglePaid(pass)}
                      disabled={updatingPaidId === pass._id}
                      title={pass.paid === false ? 'Click to mark as paid' : 'Click to mark as not paid'}
                      className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer hover:opacity-90 disabled:opacity-50 ${
                        pass.paid === false ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {updatingPaidId === pass._id ? '…' : (pass.paid === false ? 'Unpaid' : 'Paid')}
                    </button>
                    {pass.isAdmin && (
                      <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">Test</span>
                    )}
                  </div>
                </div>
                <p className="text-sm font-medium text-gray-800">
                  {pass.passTypeId?.displayName || pass.passTypeId?.name || '—'}
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                  <span>Payment:</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleTogglePaid(pass); }}
                    disabled={updatingPaidId === pass._id}
                    title={pass.paid === false ? 'Click to mark as paid' : 'Click to mark as not paid'}
                    className={`text-left cursor-pointer hover:opacity-90 disabled:opacity-50 ${pass.paid === false ? 'text-amber-700 font-medium' : ''}`}
                  >
                    {updatingPaidId === pass._id ? '…' : (pass.paid === false ? 'Unpaid' : 'Paid')}
                  </button>
                  <span>Purchased:</span>
                  <span>{formatDate(pass.purchasedAt) ?? '—'}</span>
                  <span>Expires:</span>
                  <span>{formatDate(pass.expiresAt) ?? '—'}</span>
                  <span>Classes:</span>
                  <span>
                    {pass.classesIncluded != null
                      ? `${pass.classesUsed ?? 0} / ${pass.classesIncluded}`
                      : '∞'}
                  </span>
                  <span>Price:</span>
                  <span>{pass.finalPrice != null ? formatPassPriceShort(pass.finalPrice, pass.currency) : '—'}</span>
                  {pass.isSubscription && (
                    <>
                      <span>Subscription:</span>
                      <span
                        className={pass.subscriptionActive ? 'text-green-700' : 'text-gray-500'}
                      >
                        {pass.subscriptionActive ? 'Yes' : 'No'}
                      </span>
                    </>
                  )}
                </div>
                <div className="pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDeletePass(pass._id); }}
                    disabled={deletingId === pass._id}
                    className="text-red-600 hover:underline text-xs disabled:opacity-50 touch-manipulation"
                  >
                    {deletingId === pass._id ? 'Deleting…' : 'Delete pass'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {passes.length === 0 && !loading && (
            <p className="text-center py-6 text-gray-500 text-sm">No passes match the filter.</p>
          )}

          {pagination && pagination.pages > 1 && (
            <div className="flex flex-wrap gap-2 mt-4 justify-center items-center">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-2 sm:px-4 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 touch-manipulation"
              >
                Previous
              </button>
              <span className="px-2 sm:px-4 py-2 text-sm">
                Page {pagination.page} of {pagination.pages} ({pagination.total} total)
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pagination.pages}
                className="px-3 py-2 sm:px-4 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 touch-manipulation"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedUserId && (
        <UserDetailsModal
          userDetails={userDetails}
          detailsLoading={detailsLoading}
          onClose={closeUserModal}
          onRefresh={() => {
            closeUserModal();
            fetchPasses();
          }}
          token={token}
        />
      )}

      {showAddPass && !addPassUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add pass – Select user</h3>
              <button
                type="button"
                onClick={() => setShowAddPass(false)}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            <input
              type="text"
              placeholder="Search users..."
              value={addPassUserSearch}
              onChange={(e) => setAddPassUserSearch(e.target.value)}
              className="w-full border border-gray-300 rounded p-2 text-sm mb-3"
            />
            <div className="overflow-y-auto flex-1 min-h-0 border border-gray-200 rounded">
              {addPassUserLoading ? (
                <p className="p-4 text-center text-gray-500 text-sm">Loading…</p>
              ) : addPassUserList.length === 0 ? (
                <p className="p-4 text-center text-gray-500 text-sm">No users found.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {addPassUserList.map((u) => (
                    <li key={u._id} className="flex justify-between items-center gap-2 p-3 hover:bg-gray-50">
                      <span className="text-sm truncate">
                        {u.name || u.email || '—'} {u.email && u.name ? `(${u.email})` : ''}
                      </span>
                      <button
                        type="button"
                        onClick={() => setAddPassUserId(u.user_id)}
                        className="shrink-0 px-3 py-1 bg-brown text-white rounded text-sm hover:opacity-90"
                      >
                        Select
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddPass && addPassUserId && (
        <AddPassModal
          userId={addPassUserId}
          onClose={() => setAddPassUserId(null)}
          onSuccess={() => {
            setShowAddPass(false);
            setAddPassUserId(null);
            fetchPasses();
          }}
        />
      )}

      {selectedPass && (
        <EditPassModal
          pass={selectedPass}
          token={token}
          onClose={() => setSelectedPass(null)}
          onSuccess={() => fetchPasses()}
          onDelete={handleDeletePassAndClose}
          onTogglePaid={handleTogglePaid}
          deletingId={deletingId}
          updatingPaidId={updatingPaidId}
        />
      )}
    </div>
  );
}
